/**
 * AuthorizedContactsDrawer — gestão dos contatos autorizados de um Bond
 * (LGPD ADR-129), surface na aba Cliente da conversa (bondId em mãos).
 *
 * Lista (toggle incluir revogados) + adicionar (personId honest-defer + escopos
 * + validade) + por contato ativo editar/revogar (AuthorizedContactCard).
 * Reutiliza o Drawer do DS; conteúdo escopado sob `.bac-scope` (CSS co-located).
 *
 * Gate `bond-basic` aplicado no chamador (TabCliente) + server-side.
 */

import { Drawer } from '@lume/ui-components';
import { useState } from 'react';

import {
  useAddAuthorizedContact,
  useManagedAuthorizedContacts,
} from '../../../hooks/use-bond-authorized-contacts';

import './authorized-contacts.css';
import {
  buildAddRequest,
  initialAddForm,
  toggleScope,
  validateAddForm,
  type AuthorizedContactFormState,
} from './authorized-contacts-form';
import { AuthorizedContactCard } from './AuthorizedContactCard';
import { ScopePicker } from './ScopePicker';

/**
 * Conteúdo (sem o Drawer) — reutilizável tanto pelo AuthorizedContactsDrawer
 * (aba Cliente, 1 bond) quanto pelo BondManagementDrawer com abas (ficha da
 * Pessoa, N bonds). Recebe `bondId` já resolvido (não-nulo).
 */
export function AuthorizedContactsPanel({ bondId }: { bondId: string }): JSX.Element {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [adding, setAdding] = useState(false);

  const {
    data: contacts,
    isLoading,
    isError,
  } = useManagedAuthorizedContacts(bondId, includeRevoked);

  return (
    <div className="bac-scope">
      <p className="bac-intro">
        Terceiros que o titular autorizou a agir em seu nome neste vínculo (LGPD · ADR-129). Cada
        autorização tem escopo e validade, e a revogação exige motivo.
      </p>

      <div className="bac-toolbar">
        <label className="bac-toggle">
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
          />
          <span>Incluir revogados</span>
        </label>
        {!adding && (
          <button
            type="button"
            className="bac-btn bac-btn--primary"
            onClick={() => setAdding(true)}
          >
            + Adicionar contato
          </button>
        )}
      </div>

      {adding && <AddContactForm bondId={bondId} onDone={() => setAdding(false)} />}

      {isLoading ? (
        <p className="bac-muted">Carregando contatos…</p>
      ) : isError ? (
        <p className="bac-alert bac-alert--error" role="alert">
          Não foi possível carregar os contatos autorizados.
        </p>
      ) : contacts.length === 0 ? (
        <p className="bac-empty">
          Nenhum contato autorizado{includeRevoked ? '' : ' ativo'} neste vínculo.
        </p>
      ) : (
        <div className="bac-list">
          {contacts.map((c) => (
            <AuthorizedContactCard key={c.id} bondId={c.bondId} contact={c} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AuthorizedContactsDrawer({
  bondId,
  onClose,
}: {
  bondId: string | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Drawer
      isOpen={bondId !== null}
      onClose={onClose}
      title="Contatos autorizados do vínculo"
      width="lg"
    >
      {bondId !== null && <AuthorizedContactsPanel bondId={bondId} />}
    </Drawer>
  );
}

function AddContactForm({ bondId, onDone }: { bondId: string; onDone: () => void }): JSX.Element {
  const add = useAddAuthorizedContact(bondId);
  const [form, setForm] = useState<AuthorizedContactFormState>(initialAddForm);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(): void {
    const v = validateAddForm(form);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    add.mutate(buildAddRequest(form), {
      onSuccess: onDone,
      onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao adicionar.'),
    });
  }

  return (
    <div className="bac-inline bac-inline--add">
      <label className="bac-field">
        <span className="bac-field__label">
          ID da pessoa autorizada
          <span className="bac-defer-tag">person-picker diferido</span>
        </span>
        <input
          className="bac-input"
          placeholder="person_…"
          value={form.personId}
          onChange={(e) => setForm((p) => ({ ...p, personId: e.target.value }))}
        />
      </label>

      <span className="bac-field__label">Escopos da autorização</span>
      <ScopePicker
        selected={form.scopes}
        onToggle={(s) => setForm((p) => ({ ...p, scopes: toggleScope(p.scopes, s) }))}
        idPrefix="add"
      />

      <label className="bac-field">
        <span className="bac-field__label">Validade (opcional)</span>
        <input
          type="date"
          className="bac-input"
          value={form.expiresAt}
          onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
        />
      </label>

      {error !== null && (
        <p className="bac-alert bac-alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="bac-inline__actions">
        <button type="button" className="bac-btn bac-btn--ghost" onClick={onDone}>
          Cancelar
        </button>
        <button
          type="button"
          className="bac-btn bac-btn--primary"
          disabled={add.isPending}
          onClick={handleSubmit}
        >
          {add.isPending ? 'Adicionando…' : 'Adicionar autorização'}
        </button>
      </div>
    </div>
  );
}
